import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";

import InteractiveHero from "../components/landing/interactive/InteractiveHero";
import FAQSection from "../components/landing/modern/FAQSection";
import ModernFooter from "../components/landing/modern/ModernFooter";
import Navbar from "../components/landing/modern/Navbar";
import PricingSection from "../components/landing/modern/PricingSection";
import StoryDrivenSections from "../components/landing/modern/StoryDrivenSections";
import SEOHead from "../components/tools/SEOHead";

const ModernLandingPage: React.FC = () => {
  const { t } = useTranslation();
  const text = (key: string, fallback: string) => String(t(key, fallback) ?? fallback);

  useEffect(() => {
    const previousScrollRestoration = window.history.scrollRestoration;
    const previousScrollBehavior = document.documentElement.style.scrollBehavior;
    window.history.scrollRestoration = "manual";
    document.documentElement.classList.remove("modern-landing-snap");
    document.documentElement.style.scrollBehavior = "auto";
    window.requestAnimationFrame(() => {
      window.scrollTo(0, 0);
    });
    const snapTimer = window.setTimeout(() => {
      window.scrollTo(0, 0);
      document.documentElement.classList.add("modern-landing-snap");
      window.requestAnimationFrame(() => {
        window.scrollTo(0, 0);
        document.documentElement.style.scrollBehavior = previousScrollBehavior;
      });
    }, 650);

    return () => {
      window.clearTimeout(snapTimer);
      document.documentElement.classList.remove("modern-landing-snap");
      document.documentElement.style.scrollBehavior = previousScrollBehavior;
      window.history.scrollRestoration = previousScrollRestoration;
    };
  }, []);

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "name": "YouWo",
        "url": "https://youwo.ai",
        "logo": "https://youwo.ai/logo/youwoai-logo.png",
        "sameAs": [
          "https://twitter.com/youwoai",
          "https://www.linkedin.com/company/youwoai"
        ],
        "contactPoint": {
          "@type": "ContactPoint",
          "email": "support@youwo.ai",
          "contactType": text("modern.workBrain.seo.contactType", "customer support")
        }
      },
      {
        "@type": "SoftwareApplication",
        "name": text("modern.workBrain.seo.softwareName", "YouWo - AI Memory Layer and Agent Automation Platform"),
        "applicationCategory": "ProductivityApplication",
        "operatingSystem": "Web, iOS, Android, macOS",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "USD",
          "description": text("modern.workBrain.seo.offerDescription", "Free tier available with premium and enterprise plans")
        },
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.8",
          "ratingCount": "1000",
          "bestRating": "5"
        },
        "description": text("modern.workBrain.seo.softwareDescription", "AI note and study platform for recordings, PDFs, videos, audio, images, links, and documents. Record, import, ask agents, create drafts, and study from your own sources.")
      },
      {
        "@type": "WebSite",
        "url": "https://youwo.ai",
        "name": "YouWo",
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://youwo.ai/tools?q={search_term_string}",
          "query-input": "required name=search_term_string"
        }
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white selection:bg-blue-100 selection:text-blue-900">
      <SEOHead
        title={text("modern.workBrain.seo.title", "YouWo - Record, Import, Create, and Study with AI")}
        description={text("modern.workBrain.seo.description", "Record lectures, import PDFs, videos, audio, images, and links, then ask agents to create notes, drafts, quizzes, and flashcards from your sources.")}
        keywords={text("modern.workBrain.seo.keywords", "AI notes, AI study app, lecture recorder, PDF summarizer, AI flashcards, AI quiz generator, YouTube notes, AI agent for students")}
        canonicalUrl="https://youwo.ai/"
        ogImage="https://youwo.ai/thumbnail.png"
        structuredData={structuredData}
      />
      <Navbar />
      <InteractiveHero />
      <StoryDrivenSections />
      <PricingSection />
      <FAQSection />
      <ModernFooter />
    </div>
  );
};

export default ModernLandingPage;
